import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Header } from '../components/Header/Header';
import { SignatureSelector } from '../components/SignatureSelector/SignatureSelector';
import { SignatureDisplay } from '../components/SignatureDisplay/SignatureDisplay';
import { signatureService } from '../services/api/signatureService';
import { storage } from '../utils/storage';
import {
  SignatureData,
  SigningPageData,
  ExistingSignature,
} from '../types/signature.types';
import './SigningPage.scss';

export function SigningPage() {
  const { sessionToken } = useParams<{ sessionToken: string }>();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState<SigningPageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [showSelector, setShowSelector] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState<number | null>(null);

  useEffect(() => {
    const loadSession = async () => {
      if (!sessionToken) {
        setError('Invalid signing link.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        storage.setSessionToken(sessionToken);
        const data = await signatureService.getSigningPageData(sessionToken);
        setPageData(data);

        if (data.existing_signatures.length === 1) {
          setSelectedExisting(0);
        }
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 410 || status === 404) {
          navigate('/expired', { replace: true });
        } else {
          console.error('Failed to load signing session:', err);
          const backendMessage = err?.response?.data?.message || err?.message;
          setError(backendMessage || 'Failed to load signing session. Please try again.');
          setErrorDetails(err?.response?.data?.details || null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
  }, [sessionToken, navigate]);

  const existingToSignatureData = (existing: ExistingSignature): SignatureData => ({
    method: existing.type,
    textSignature: existing.text || undefined,
    digitalSignature: existing.preview || undefined,
  });

  const handleUseExisting = useCallback(async () => {
    if (!sessionToken || selectedExisting === null || !pageData) return;

    const existing = pageData.existing_signatures[selectedExisting];
    if (!existing) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await signatureService.submitSignature(
        sessionToken,
        existingToSignatureData(existing),
        true
      );
      navigate(`/sign/${sessionToken}/success`, { replace: true });
    } catch (err: any) {
      console.error('Failed to submit signature:', err);
      const backendMessage = err?.response?.data?.message || err?.message;
      setError(backendMessage || 'Failed to submit signature. Please try again.');
      setErrorDetails(err?.response?.data?.details || null);
    } finally {
      setIsSubmitting(false);
    }
  }, [sessionToken, selectedExisting, pageData, navigate]);

  const handleNewSignatureComplete = useCallback(
    async (signatureData: SignatureData) => {
      if (!sessionToken) return;

      try {
        setIsSubmitting(true);
        setError(null);
        await signatureService.submitSignature(sessionToken, signatureData, false);
        navigate(`/sign/${sessionToken}/success`, { replace: true });
      } catch (err: any) {
        console.error('Failed to submit signature:', err);
        const backendMessage = err?.response?.data?.message || err?.message;
        setError(backendMessage || 'Failed to submit signature. Please try again.');
        setErrorDetails(err?.response?.data?.details || null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [sessionToken, navigate]
  );

  const hasExistingSignatures =
    pageData && pageData.existing_signatures.length > 0;

  if (isLoading) {
    return (
      <div className="signing-page">
        <Header />
        <div className="signing-page__loading">
          <CircularProgress sx={{ color: '#7db8e0' }} />
          <p>Loading your signing session...</p>
        </div>
      </div>
    );
  }

  if (error && !pageData) {
    return (
      <div className="signing-page">
        <Header />
        <div className="signing-page__error">
          <div className="signing-page__error-card">
            <ErrorOutlineIcon className="signing-page__error-icon" />
            <h2 className="signing-page__error-title">{error}</h2>
            {errorDetails && (
              <p className="signing-page__error-message">{errorDetails}</p>
            )}
            <p className="signing-page__error-note">
              If you believe this is a mistake, please contact your clinic.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signing-page">
      <Header />
      <div className="signing-page__content">
        <div className="signing-page__info">
          <h2 className="signing-page__patient-name">
            {pageData?.patient_name}
          </h2>
          <p className="signing-page__care-plan">
            Document: <strong>{pageData?.document_type}</strong>
            {pageData?.clinic_name && (
              <> &mdash; {pageData.clinic_name}</>
            )}
          </p>
        </div>

        <p className="signing-page__hint">
          Please review your details above, then sign below.
        </p>

        {error && (
          <div className="signing-page__error-banner">
            <p>{error}</p>
          </div>
        )}

        {hasExistingSignatures && !showSelector && (
          <div className="signing-page__existing">
            <h3 className="signing-page__existing-title">
              {pageData!.existing_signatures.length === 1
                ? 'Your Existing Signature'
                : 'Your Existing Signatures'}
            </h3>
            <p className="signing-page__existing-hint">
              {pageData!.existing_signatures.length === 1
                ? 'Tap "Use This Signature" to continue, or create a new one.'
                : 'Tap a signature to select it, or create a new one.'}
            </p>

            <div className="signing-page__existing-list">
              {pageData!.existing_signatures.map((sig, index) => (
                <div
                  key={index}
                  className={`signing-page__existing-item ${
                    selectedExisting === index ? 'selected' : ''
                  }`}
                  onClick={() => setSelectedExisting(index)}
                >
                  <SignatureDisplay
                    method={sig.type}
                    textSignature={sig.text || undefined}
                    digitalSignature={sig.preview || undefined}
                    label={
                      pageData!.existing_signatures.length > 1
                        ? `Signature ${index + 1} (${sig.type})`
                        : 'Your Existing Signature'
                    }
                  />
                </div>
              ))}
            </div>

            <div className="signing-page__existing-actions">
              <button
                type="button"
                className="signing-page__use-existing-btn"
                onClick={handleUseExisting}
                disabled={isSubmitting || selectedExisting === null}
              >
                {isSubmitting ? 'Submitting...' : 'Use This Signature'}
              </button>
              <button
                type="button"
                className="signing-page__new-signature-btn"
                onClick={() => setShowSelector(true)}
              >
                Create New Signature
              </button>
            </div>
          </div>
        )}

        {(!hasExistingSignatures || showSelector) && (
          <div className="signing-page__selector">
            {isSubmitting && (
              <div className="signing-page__submitting-overlay">
                <CircularProgress sx={{ color: '#7db8e0' }} />
                <p>Submitting your signature...</p>
              </div>
            )}
            <SignatureSelector
              patientName={pageData?.patient_name || ''}
              onSignatureComplete={handleNewSignatureComplete}
              onCancel={
                hasExistingSignatures
                  ? () => setShowSelector(false)
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default SigningPage;
